/**
 * UIScene — parallel HUD overlay scene.
 *
 * Runs alongside OverworldScene or GameScene (whichever world scene is
 * currently active) and owns camera-fixed HUD panels. See
 * docs/design/scene-refactor-plan.md for the full design.
 *
 * Panels migrated so far:
 *   - PauseOverlay (step 2)
 *   - SettingsMenu (step 3)
 *   - InventoryPanel (step 4)
 *
 * Lifecycle contract (do NOT regress commit d01c892):
 *   - Every `.on(...)` registered in `create()` has a matching `.off(...)`
 *     in `shutdown()`. Helpers in `game/HudState.ts` return unsubscribers
 *     that we collect and drain in shutdown().
 *   - UIScene does NOT hold direct references to other scenes as fields;
 *     cross-scene data flows through `game.events` (edges) and
 *     `game.registry` (snapshots), encapsulated in `game/HudState.ts`.
 *   - Launched idempotently via the static `ensureLaunched` helper so double
 *     `scene.launch('UIScene')` never duplicates any subscriptions.
 */
import * as Phaser from 'phaser';
import type { AudioSettings } from '../audio/AudioManager';
import { devLog } from '../debug/devLog';
import { InventoryPanel } from '../ui/InventoryPanel';
import { HearthChatPanel } from '../ui/HearthChatPanel';
import { PauseOverlay } from '../ui/PauseOverlay';
import { SettingsMenu } from '../ui/SettingsMenu';
import {
  emitInventoryClosed,
  emitInventoryOpened,
  emitHearthChatClosed,
  emitHearthChatOpened,
  emitHearthChatSend,
  emitHearthChatInputFocused,
  emitHearthChatInputBlurred,
  emitPauseQuit,
  emitPauseResumed,
  emitPauseShow,
  emitSettingsClosed,
  emitSettingsOpened,
  emitTouchControlsChanged,
  emitDebugOverlayChanged,
  getAudioGateway,
  getInventorySnapshotProvider,
  onHearthChatToggle,
  onHearthChatHide,
  onHearthChatMessage,
  onInventoryHide,
  onInventoryToggle,
  onPauseHide,
  onPauseShow,
  onSettingsClose,
  onSettingsHide,
  type HudUnsub,
} from '../game/HudState';

/** Fallback audio values used before any world scene publishes its gateway.
 *  The first `show()` of SettingsMenu calls refreshSliders which re-reads
 *  the live gateway, so these values are never user-visible in practice. */
const FALLBACK_AUDIO_SETTINGS: AudioSettings = {
  masterVolume: 1,
  sfxVolume: 1,
  musicVolume: 1,
  muted: false,
};

export class UIScene extends Phaser.Scene {
  public static readonly KEY = 'UIScene';

  private inventoryPanel: InventoryPanel | null = null;
  private hearthChatPanel: HearthChatPanel | null = null;
  private pauseOverlay: PauseOverlay | null = null;
  private settingsMenu: SettingsMenu | null = null;

  /** Unsubscribers for every `game.events` listener registered in create().
   *  Drained in shutdown() so we never leak a handler. */
  private readonly unsubs: HudUnsub[] = [];

  constructor() {
    super({ key: UIScene.KEY });
  }

  /**
   * Idempotent launcher. Call from a world scene's `create()` after the
   * scene has been added to the SceneManager (i.e. it's in game.config.ts
   * scene array). Safe to call multiple times — subsequent calls are no-ops.
   */
  public static ensureLaunched(scene: Phaser.Scene): void {
    if (scene.scene.isActive(UIScene.KEY)) return;
    scene.scene.launch(UIScene.KEY);
  }

  create(): void {
    devLog('[UIScene] create');

    // Settings menu — wired to the AudioGateway the active world scene
    // publishes in the registry. Every callback looks up the live gateway
    // at call time so the panel follows the active world scene across
    // overworld↔dungeon transitions without re-wiring.
    this.settingsMenu = new SettingsMenu(this);
    this.settingsMenu.wireAudio(
      () => getAudioGateway(this.game)?.getSettings() ?? FALLBACK_AUDIO_SETTINGS,
      (v) => getAudioGateway(this.game)?.setMasterVolume(v),
      (v) => getAudioGateway(this.game)?.setSfxVolume(v),
      (v) => getAudioGateway(this.game)?.setMusicVolume(v),
      () => getAudioGateway(this.game)?.persist(),
    );
    this.settingsMenu.setOnClose(() => {
      // User clicked Back. Settings already hid itself internally; we
      // notify the world scene and reshow the pause overlay.
      emitSettingsClosed(this.game);
      emitPauseShow(this.game);
    });
    // AGE-955: Forward touch controls toggle to world scene
    this.settingsMenu.setOnTouchControlsChanged((visible) => {
      emitTouchControlsChanged(this.game, visible);
    });
    // AGE-1471: Forward debug overlay toggle to painted scenes
    this.settingsMenu.setOnDebugOverlayChanged((visible) => {
      emitDebugOverlayChanged(this.game, visible);
    });

    this.pauseOverlay = new PauseOverlay(this);
    this.pauseOverlay.setOnResume(() => {
      this.pauseOverlay?.hide();
      emitPauseResumed(this.game);
    });
    this.pauseOverlay.setOnSettings(() => {
      // Transition pause → settings entirely inside UIScene.
      this.pauseOverlay?.hide();
      this.settingsMenu?.show();
      emitSettingsOpened(this.game);
    });
    this.pauseOverlay.setOnQuitLobby(() => {
      this.pauseOverlay?.hide();
      emitPauseQuit(this.game);
    });

    // Inventory panel — reads equipment/weight from the snapshot provider
    // the active world scene publishes to the registry.
    this.inventoryPanel = new InventoryPanel(this);
    this.inventoryPanel.setSnapshotProvider(
      () => getInventorySnapshotProvider(this.game)?.() ?? null,
    );

    // Hearth chat panel — proximity-bound ephemeral whisper chat (AGE-1227).
    this.hearthChatPanel = new HearthChatPanel(this);
    this.hearthChatPanel.setOnSend((content) => {
      emitHearthChatSend(this.game, content);
      emitHearthChatInputBlurred(this.game);
    });

    // Enter key activates the chat input when the panel is visible.
    const enterKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER);
    enterKey.on('down', () => {
      if (this.hearthChatPanel?.isVisible() && !this.hearthChatPanel.isInputFocused()) {
        this.hearthChatPanel.activateInput();
        emitHearthChatInputFocused(this.game);
      }
    });

    this.unsubs.push(
      onPauseShow(this.game, () => this.pauseOverlay?.show()),
      onPauseHide(this.game, () => this.pauseOverlay?.hide()),
      onSettingsHide(this.game, () => {
        if (!this.settingsMenu?.isVisible()) return;
        this.settingsMenu.hide();
        emitSettingsClosed(this.game);
      }),
      onSettingsClose(this.game, () => {
        if (!this.settingsMenu?.isVisible()) return;
        this.settingsMenu.hide();
        emitSettingsClosed(this.game);
        emitPauseShow(this.game);
      }),
      onInventoryToggle(this.game, () => {
        if (!this.inventoryPanel) return;
        this.inventoryPanel.toggle();
        if (this.inventoryPanel.isVisible()) {
          emitInventoryOpened(this.game);
        } else {
          emitInventoryClosed(this.game);
        }
      }),
      onInventoryHide(this.game, () => {
        if (!this.inventoryPanel?.isVisible()) return;
        this.inventoryPanel.hide();
        emitInventoryClosed(this.game);
      }),
      onHearthChatToggle(this.game, () => {
        if (!this.hearthChatPanel) return;
        this.hearthChatPanel.toggle();
        if (this.hearthChatPanel.isVisible()) {
          emitHearthChatOpened(this.game);
        } else {
          emitHearthChatClosed(this.game);
        }
      }),
      onHearthChatHide(this.game, () => {
        if (!this.hearthChatPanel?.isVisible()) return;
        this.hearthChatPanel.hide();
        emitHearthChatClosed(this.game);
      }),
      onHearthChatMessage(this.game, (event) => {
        this.hearthChatPanel?.addMessage(event);
      }),
    );
  }

  update(): void {
    this.hearthChatPanel?.update();
  }

  shutdown(): void {
    devLog('[UIScene] shutdown');

    for (const unsub of this.unsubs) unsub();
    this.unsubs.length = 0;

    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    this.settingsMenu?.destroy();
    this.settingsMenu = null;
    this.inventoryPanel?.destroy();
    this.inventoryPanel = null;
    this.hearthChatPanel?.destroy();
    this.hearthChatPanel = null;
  }
}
